import type { SceneDef } from "../../genesis/schema";

/**
 * Une Vie d'Impact — Scène 3 : Les Quatre Saisons de vie (ordre 3).
 * Scène : le Jardin des Saisons — un grand jardin en croix dont chaque
 * quart vit une saison : le PRINTEMPS des études (bosquet vert tendre),
 * l'ÉTÉ de la carrière (blés dorés), l'AUTOMNE de la famille (feuillages
 * roux), l'HIVER de l'aînesse (arbres argentés et lanternes). Au centre,
 * la fontaine des saisons, le cadran solaire et la cloche du soir.
 * Dans chaque saison, un CHOIX D'ESSENTIEL (sphère d'or) à déposer sur
 * l'autel de la saison — jamais sur le mauvais choix qui lui fait face
 * (divan des distractions, idole de la réussite, trône du surmenage,
 * coffre des regrets). Un témoin biblique habite chaque saison : Daniel
 * l'étudiant, Néhémie le bâtisseur, Anne la mère, Siméon l'aîné.
 * 4 fruits de saison et 4 sabliers à ramasser rythment le parcours.
 * Ancrages : Mt 6:33 ; Ec 3:1 ; Ps 90:12 ; Pr 9:10 ; Ps 71:18.
 * Mécanique signature : à chaque saison de la vie, un seul essentiel —
 * chercher premièrement le royaume (Mt 6:33).
 */
export const vieDimpactScene3: SceneDef = {
  id: "vie-dimpact/scene-3",
  game: "vie-dimpact",
  title: { fr: "Les Quatre Saisons de vie — Le Jardin des Saisons", en: "The Four Seasons of Life — The Garden of Seasons" },
  order: 3,
  keyName: { fr: "Clé de l'Intendance Fidèle", en: "Key of Faithful Stewardship" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 64,
    skyColor: "#dcd0ec",
    groundColor: "#6f8a4e",
    lighting: "day",
    props: [
      // Bosquet vert tendre du printemps (quart nord-ouest).
      { id: "bosquet_printemps", shape: "tree", pos: [-16, 0, -16], color: "#5fae57", size: 1.5, scatter: 10, scatterRadius: 8 },
      // Fleurs roses du printemps.
      { id: "fleurs_printemps", shape: "sphere", pos: [-14, 0, -12], color: "#e8a8c8", size: 0.25, scatter: 16, scatterRadius: 8 },
      // Blés dorés de l'été (quart nord-est).
      { id: "bles_ete", shape: "cone", pos: [16, 0, -14], color: "#e0bd5f", size: 1, scatter: 14, scatterRadius: 8 },
      // Arbres secs et chauds de l'été.
      { id: "arbres_ete", shape: "tree", pos: [20, 0, -18], color: "#8a9a3f", size: 1.4, scatter: 8, scatterRadius: 7 },
      // Feuillages roux de l'automne (quart sud-est).
      { id: "arbres_automne", shape: "tree", pos: [16, 0, 16], color: "#c07f3f", size: 1.5, scatter: 12, scatterRadius: 8 },
      // Feuilles mortes qui jonchent le sol d'automne.
      { id: "feuilles_automne", shape: "sphere", pos: [13, 0, 13], color: "#b56a35", size: 0.2, scatter: 18, scatterRadius: 9 },
      // Arbres argentés de l'hiver (quart sud-ouest).
      { id: "arbres_hiver", shape: "tree", pos: [-16, 0, 16], color: "#9aa8b0", size: 1.4, scatter: 10, scatterRadius: 8 },
      // Lanternes douces de l'hiver.
      { id: "lanternes_hiver", shape: "flame", pos: [-14, 0, 18], color: "#cfe0e8", size: 0.4, scatter: 6, scatterRadius: 7 },
      // Étang paisible au nord du jardin.
      { id: "etang_central", shape: "water", pos: [0, 0, -24], color: "#5b8fa8", size: 3.5 },
      // Sentiers de pierre qui relient les quatre saisons.
      { id: "sentiers", shape: "stele", pos: [0, 0, 0], color: "#9b9080", size: 0.22, scatter: 24, scatterRadius: 20 },
      // Promeneurs de tous les âges dans les allées.
      { id: "promeneurs", shape: "person", pos: [0, 0, 6], color: "#6d7f9d", size: 1, scatter: 12, scatterRadius: 14 },
      // Collines lointaines derrière l'étang.
      { id: "collines_lointaines", shape: "mountain", pos: [0, 0, -28], color: "#8a9a6b", size: 5, scatter: 4, scatterRadius: 8 },
    ],
  },
  spawn: [0, 0, 20],
  difficulty: {
    // 15 interactables avançants : 4 choix d'essentiel (drag_drop)
    // + 4 fruits + 4 sabliers (pickup) + 3 leviers (lever_action).
    D: { goalCount: 5, hints: true },
    A: { goalCount: 10, hints: true },
    E: { goalCount: 15, hints: false, timerS: 300 },
  },
  interactables: [
    // ——— PRINTEMPS — les études (nord-ouest) ———
    { id: "pupitre_etudes", type: "build_slot", label: { fr: "Le Pupitre des études — autel du printemps", en: "The Study Desk — altar of spring" }, pos: [-16, 0, -16], shape: "box", color: "#6d8bb0", size: 2.2, targetId: "pupitre_etudes" },
    { id: "choix_sagesse", type: "drag_drop", label: { fr: "Choix d'essentiel : la sagesse de Dieu d'abord (Pr 9:10)", en: "Essential choice: God's wisdom first (Pr 9:10)" }, pos: [-10, 0, -12], shape: "sphere", color: "#d4af37", size: 0.9, targetId: "pupitre_etudes", count: 1 },
    { id: "divan_distractions", type: "build_slot", label: { fr: "Le Divan des distractions (mauvais choix)", en: "The Couch of Distractions (bad choice)" }, pos: [-22, 0, -10], shape: "box", color: "#4a3f35", size: 1.6, targetId: "divan_distractions" },
    { id: "npc_etudiant", type: "npc_dialog", label: { fr: "Daniel l'étudiant résolu (Dn 1:8)", en: "Daniel the resolute student (Dn 1:8)" }, pos: [-13, 0, -20], shape: "person", color: "#4f6b8f", size: 1.2 },
    { id: "fruit_printemps", type: "pickup", label: { fr: "Fruit du printemps — l'émerveillement", en: "Fruit of spring — wonder" }, pos: [-20, 0, -20], shape: "sphere", color: "#8fce6a", size: 0.6, count: 1 },
    // ——— ÉTÉ — la carrière (nord-est) ———
    { id: "etabli_carriere", type: "build_slot", label: { fr: "L'Établi de la carrière — autel de l'été", en: "The Work Bench — altar of summer" }, pos: [16, 0, -16], shape: "box", color: "#a9743f", size: 2.2, targetId: "etabli_carriere" },
    { id: "choix_integrite", type: "drag_drop", label: { fr: "Choix d'essentiel : l'intégrité au travail (Col 3:23)", en: "Essential choice: integrity at work (Col 3:23)" }, pos: [10, 0, -12], shape: "sphere", color: "#d4af37", size: 0.9, targetId: "etabli_carriere", count: 1 },
    { id: "idole_reussite", type: "build_slot", label: { fr: "L'Idole de la réussite (mauvais choix)", en: "The Idol of Success (bad choice)" }, pos: [22, 0, -10], shape: "cylinder", color: "#4a3f35", size: 1.6, targetId: "idole_reussite" },
    { id: "npc_batisseur", type: "npc_dialog", label: { fr: "Néhémie le bâtisseur (Né 2:18)", en: "Nehemiah the builder (Ne 2:18)" }, pos: [13, 0, -20], shape: "person", color: "#7a6b3f", size: 1.2 },
    { id: "fruit_ete", type: "pickup", label: { fr: "Fruit de l'été — la persévérance", en: "Fruit of summer — perseverance" }, pos: [20, 0, -20], shape: "sphere", color: "#e0bd5f", size: 0.6, count: 1 },
    // ——— AUTOMNE — la famille (sud-est) ———
    { id: "foyer_famille", type: "build_slot", label: { fr: "Le Foyer de la famille — autel de l'automne", en: "The Family Hearth — altar of autumn" }, pos: [16, 0, 16], shape: "box", color: "#8f6b4a", size: 2.2, targetId: "foyer_famille" },
    { id: "choix_presence", type: "drag_drop", label: { fr: "Choix d'essentiel : la présence aux siens (Jos 24:15)", en: "Essential choice: presence with your own (Jos 24:15)" }, pos: [10, 0, 12], shape: "sphere", color: "#d4af37", size: 0.9, targetId: "foyer_famille", count: 1 },
    { id: "trone_surmenage", type: "build_slot", label: { fr: "Le Trône du surmenage (mauvais choix)", en: "The Throne of Overwork (bad choice)" }, pos: [22, 0, 10], shape: "cylinder", color: "#4a3f35", size: 1.6, targetId: "trone_surmenage" },
    { id: "npc_parent", type: "npc_dialog", label: { fr: "Anne la mère fidèle (1 S 1:27-28)", en: "Hannah the faithful mother (1 Sa 1:27-28)" }, pos: [13, 0, 20], shape: "person", color: "#9f6b5a", size: 1.2 },
    { id: "fruit_automne", type: "pickup", label: { fr: "Fruit de l'automne — la gratitude", en: "Fruit of autumn — gratitude" }, pos: [20, 0, 22], shape: "sphere", color: "#c07f3f", size: 0.6, count: 1 },
    // ——— HIVER — l'aînesse (sud-ouest) ———
    { id: "banc_transmission", type: "build_slot", label: { fr: "Le Banc de la transmission — autel de l'hiver", en: "The Bench of Passing On — altar of winter" }, pos: [-16, 0, 16], shape: "box", color: "#7d7468", size: 2.2, targetId: "banc_transmission" },
    { id: "choix_transmission", type: "drag_drop", label: { fr: "Choix d'essentiel : transmettre la foi (Ps 71:18)", en: "Essential choice: pass on the faith (Ps 71:18)" }, pos: [-10, 0, 12], shape: "sphere", color: "#d4af37", size: 0.9, targetId: "banc_transmission", count: 1 },
    { id: "coffre_regrets", type: "build_slot", label: { fr: "Le Coffre des regrets (mauvais choix)", en: "The Chest of Regrets (bad choice)" }, pos: [-22, 0, 10], shape: "box", color: "#4a3f35", size: 1.6, targetId: "coffre_regrets" },
    { id: "npc_aine", type: "npc_dialog", label: { fr: "Siméon l'aîné qui espère (Lc 2:25)", en: "Simeon the hopeful elder (Lk 2:25)" }, pos: [-13, 0, 20], shape: "person", color: "#8a8a9d", size: 1.2 },
    { id: "fruit_hiver", type: "pickup", label: { fr: "Fruit de l'hiver — la sagesse", en: "Fruit of winter — wisdom" }, pos: [-20, 0, 22], shape: "sphere", color: "#cfe0e8", size: 0.6, count: 1 },
    // ——— Le centre du jardin ———
    { id: "fontaine_saisons", type: "lever_action", label: { fr: "La Fontaine des saisons — puise l'eau vive", en: "The Fountain of Seasons — draw living water" }, pos: [0, 0, -2], shape: "cylinder", color: "#5b8fa8", size: 1.5, holdS: 3 },
    { id: "cadran_solaire", type: "lever_action", label: { fr: "Le Cadran solaire — compte tes jours (Ps 90:12)", en: "The Sundial — number your days (Ps 90:12)" }, pos: [4, 0, -6], shape: "cylinder", color: "#b9a06a", size: 1.2, holdS: 2 },
    { id: "cloche_du_soir", type: "lever_action", label: { fr: "La Cloche du soir — rends grâce pour la saison", en: "The Evening Bell — give thanks for the season" }, pos: [-4, 0, -6], shape: "cone", color: "#8c7a54", size: 1.2, holdS: 2 },
    // ——— Les 4 sabliers du temps ———
    { id: "sablier_matin", type: "pickup", label: { fr: "Sablier du matin — le temps d'apprendre", en: "Morning hourglass — time to learn" }, pos: [-8, 0, 2], shape: "cone", color: "#e7d9a8", size: 0.5, count: 1 },
    { id: "sablier_midi", type: "pickup", label: { fr: "Sablier de midi — le temps de bâtir", en: "Noon hourglass — time to build" }, pos: [8, 0, 2], shape: "cone", color: "#e7d9a8", size: 0.5, count: 1 },
    { id: "sablier_soir", type: "pickup", label: { fr: "Sablier du soir — le temps d'aimer", en: "Evening hourglass — time to love" }, pos: [0, 0, -12], shape: "cone", color: "#e7d9a8", size: 0.5, count: 1 },
    { id: "sablier_nuit", type: "pickup", label: { fr: "Sablier de la nuit — le temps de transmettre", en: "Night hourglass — time to pass on" }, pos: [0, 0, 10], shape: "cone", color: "#e7d9a8", size: 0.5, count: 1 },
    // ——— Les 2 stèles de la Parole ———
    { id: "panel_ecc31", type: "panel", label: { fr: "Lire Ecclésiaste 3:1", en: "Read Ecclesiastes 3:1" }, pos: [24, 0, 0], shape: "stele", color: "#b9a06a", size: 1.4, panelId: "p_ecc31" },
    { id: "panel_ps9012", type: "panel", label: { fr: "Lire Psaume 90:12", en: "Read Psalm 90:12" }, pos: [-24, 0, 0], shape: "stele", color: "#b9a06a", size: 1.4, panelId: "p_ps9012" },
  ],
  objectives: [
    { id: "o_essentiel", kind: "build", label: { fr: "Dépose le choix d'essentiel de chaque saison sur son autel : pupitre, établi, foyer, banc — jamais sur les mauvais choix", en: "Place each season's essential choice on its altar: desk, bench, hearth, elder's bench — never on the bad choices" }, count: 4 },
    { id: "o_temps", kind: "collect", label: { fr: "Ramasse les 4 fruits de saison et les 4 sabliers du temps", en: "Gather the 4 season fruits and the 4 hourglasses of time" }, count: 8 },
  ],
  panels: [
    {
      id: "p_ecc31",
      title: { fr: "Un temps pour toute chose", en: "A time for every purpose" },
      reference: "Ecclésiaste 3:1",
      passage: {
        fr: "Il y a un temps pour tout, un temps pour toute chose sous les cieux.",
        en: "For everything there is a season, and a time for every purpose under heaven.",
      },
      explain: {
        fr: "Dieu a donné à la vie ses saisons : apprendre, bâtir, aimer, transmettre. Aucune saison n'est une salle d'attente — chacune porte son fruit propre. La fidélité, c'est habiter pleinement la saison présente au lieu de regretter celle d'avant ou de courir vers la suivante.",
        en: "God gave life its seasons: learning, building, loving, passing on. No season is a waiting room — each bears its own fruit. Faithfulness means fully inhabiting the present season instead of mourning the last one or rushing to the next.",
      },
      didYouKnow: {
        fr: "Le mot hébreu traduit « temps » (zeman) désigne un moment fixé, un rendez-vous : chaque saison de ta vie est un rendez-vous fixé par Dieu.",
        en: "The Hebrew word rendered 'season' (zeman) means an appointed moment: each season of your life is an appointment set by God.",
      },
      application: {
        fr: "Quelle est ta saison actuelle — études, carrière, famille, aînesse ? Quel est son fruit propre à cultiver cette semaine ?",
        en: "What is your current season — studies, career, family, eldership? What is its distinctive fruit to cultivate this week?",
      },
    },
    {
      id: "p_ps9012",
      title: { fr: "Compter nos jours", en: "Numbering our days" },
      reference: "Psaume 90:12",
      passage: {
        fr: "Enseigne-nous à bien compter nos jours, afin que nous appliquions notre cœur à la sagesse.",
        en: "So teach us to number our days, that we may gain a heart of wisdom.",
      },
      explain: {
        fr: "Compter ses jours, ce n'est pas les craindre : c'est les recevoir comme un trésor mesuré, à investir dans l'essentiel. Moïse, l'homme des quarante ans par saison, prie pour un cœur sage plutôt que pour des jours en plus. La sagesse commence quand on cesse de croire que le temps est illimité.",
        en: "Numbering your days is not fearing them: it is receiving them as a measured treasure to invest in the essential. Moses, the man of forty-year seasons, prays for a wise heart rather than for extra days. Wisdom begins when you stop believing time is unlimited.",
      },
      didYouKnow: {
        fr: "Le Psaume 90 est attribué à Moïse : sa vie eut trois saisons de quarante ans — prince en Égypte, berger à Madian, conducteur d'Israël.",
        en: "Psalm 90 is attributed to Moses: his life had three forty-year seasons — prince in Egypt, shepherd in Midian, leader of Israel.",
      },
      application: {
        fr: "Prends dix minutes pour « compter » ta semaine : quelles heures servent vraiment l'essentiel de ta saison ?",
        en: "Take ten minutes to 'number' your week: which hours truly serve the essential of your season?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_premierement", level: "D",
      prompt: { fr: "Selon Matthieu 6:33, que faut-il chercher premièrement ?", en: "According to Matthew 6:33, what must we seek first?" },
      choices: [
        { fr: "Le royaume et la justice de Dieu", en: "God's Kingdom and His righteousness" },
        { fr: "La réussite des études", en: "Success in school" },
        { fr: "La sécurité de la carrière", en: "Career security" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Cherchez premièrement le royaume et la justice de Dieu; et toutes ces choses vous seront données par-dessus » (Mt 6:33) : à chaque saison, le royaume d'abord.",
        en: "'Seek first God's Kingdom and his righteousness; and all these things will be given to you as well' (Mt 6:33): in every season, the Kingdom first.",
      },
    },
    {
      id: "q_saisons", level: "A",
      prompt: { fr: "Selon Ecclésiaste 3:1, qu'y a-t-il « pour toute chose sous les cieux » ?", en: "According to Ecclesiastes 3:1, what is there 'for every purpose under heaven'?" },
      choices: [
        { fr: "Un temps", en: "A time" },
        { fr: "Une récompense", en: "A reward" },
        { fr: "Une épreuve", en: "A trial" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Il y a un temps pour tout, un temps pour toute chose sous les cieux » (Ec 3:1) : chaque saison de la vie a son heure fixée par Dieu.",
        en: "'For everything there is a season, and a time for every purpose under heaven' (Ec 3:1): every season of life has its God-appointed hour.",
      },
    },
    {
      id: "q_jours", level: "E",
      prompt: { fr: "Selon le Psaume 90:12, pourquoi demander à Dieu de nous apprendre à bien compter nos jours ?", en: "According to Psalm 90:12, why ask God to teach us to number our days?" },
      choices: [
        { fr: "Pour mieux planifier notre retraite", en: "To plan our retirement better" },
        { fr: "Afin que nous appliquions notre cœur à la sagesse", en: "That we may gain a heart of wisdom" },
        { fr: "Pour devancer les autres", en: "To get ahead of others" },
        { fr: "Pour ne jamais vieillir", en: "So we never grow old" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Enseigne-nous à bien compter nos jours, afin que nous appliquions notre cœur à la sagesse » (Ps 90:12) : mesurer le temps conduit le cœur à choisir l'essentiel.",
        en: "'So teach us to number our days, that we may gain a heart of wisdom' (Ps 90:12): measuring time leads the heart to choose the essential.",
      },
    },
    {
      id: "qD2", level: "D",
      prompt: { fr: "Selon Proverbes 9:10, quel est le commencement de la sagesse ?", en: "According to Proverbs 9:10, what is the beginning of wisdom?" },
      choices: [
        { fr: "L'expérience des anciens", en: "The experience of the elders" },
        { fr: "La crainte de l'Éternel", en: "The fear of Yahweh" },
        { fr: "La réussite aux examens", en: "Passing one's exams" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Le commencement de la sagesse, c'est la crainte de l'Éternel » (Pr 9:10) : au printemps des études, la vraie sagesse ne commence pas par les livres mais par le respect de Dieu — le choix d'essentiel du pupitre.",
        en: "'The fear of Yahweh is the beginning of wisdom' (Pr 9:10): in the springtime of studies, true wisdom starts not with books but with reverence for God — the study desk's essential choice.",
      },
    },
    {
      id: "qD3", level: "D",
      prompt: { fr: "Selon Daniel 1:8, que résolut Daniel dans son cœur à la cour de Babylone ?", en: "According to Daniel 1:8, what did Daniel resolve in his heart at the court of Babylon?" },
      choices: [
        { fr: "De ne pas se souiller par les mets du roi", en: "Not to defile himself with the king's food" },
        { fr: "De devenir le premier des sages de Babylone", en: "To become the first of Babylon's wise men" },
        { fr: "De s'enfuir de la cour du roi", en: "To flee the king's court" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Daniel résolut de ne pas se souiller par les mets du roi et par le vin dont le roi buvait » (Dn 1:8) : l'étudiant du printemps garde l'essentiel dès sa jeunesse — c'est pourquoi Daniel est le témoin de cette saison.",
        en: "'Daniel purposed in his heart that he would not defile himself with the king's dainties, nor with the wine which he drank' (Dn 1:8): the springtime student keeps the essential from his youth — which is why Daniel is this season's witness.",
      },
    },
    {
      id: "qD4", level: "D",
      prompt: { fr: "Selon Luc 2:25, qui était Siméon, le témoin de l'hiver ?", en: "According to Luke 2:25, who was Simeon, the winter witness?" },
      choices: [
        { fr: "Un sacrificateur du temple", en: "A priest of the temple" },
        { fr: "Un roi âgé de Jérusalem", en: "An aged king of Jerusalem" },
        { fr: "Un homme juste et pieux qui attendait la consolation d'Israël", en: "A righteous and devout man waiting for the consolation of Israel" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Cet homme était juste et pieux, il attendait la consolation d'Israël, et l'Esprit-Saint était sur lui » (Lc 2:25) : l'hiver de la vie peut être une saison d'espérance vive, pas de regrets.",
        en: "'This man was righteous and devout, looking for the consolation of Israel, and the Holy Spirit was on him' (Lk 2:25): the winter of life can be a season of living hope, not of regrets.",
      },
    },
    {
      id: "qD5", level: "D",
      prompt: { fr: "Selon 1 Samuel 1:27-28, que fait Anne de l'enfant que l'Éternel lui a accordé ?", en: "According to 1 Samuel 1:27-28, what does Hannah do with the child Yahweh granted her?" },
      choices: [
        { fr: "Elle le garde caché chez elle", en: "She keeps him hidden at home" },
        { fr: "Elle le consacre à l'Éternel pour toute sa vie", en: "She dedicates him to Yahweh for his whole life" },
        { fr: "Elle l'envoie à l'école des scribes", en: "She sends him to the school of the scribes" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« C'était pour cet enfant que je priais… Aussi je veux le prêter à l'Éternel : il sera toute sa vie prêté à l'Éternel » (1 S 1:27-28) : Anne, témoin de l'automne, montre que la famille se reçoit de Dieu et se lui rend.",
        en: "'For this child I prayed… Therefore I have also given him to Yahweh. As long as he lives he is given to Yahweh' (1 Sa 1:27-28): Hannah, the autumn witness, shows that family is received from God and given back to Him.",
      },
    },
    {
      id: "qA2", level: "A",
      prompt: { fr: "Selon Matthieu 6:33, que promet Jésus à ceux qui cherchent premièrement le royaume ?", en: "According to Matthew 6:33, what does Jesus promise those who seek the Kingdom first?" },
      choices: [
        { fr: "Une vie sans aucune épreuve", en: "A life without any trial" },
        { fr: "La richesse et la célébrité", en: "Wealth and fame" },
        { fr: "Toutes ces choses données par-dessus", en: "All these things given as well" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Et toutes ces choses vous seront données par-dessus » (Mt 6:33) : Dieu pourvoit aux besoins de celui qui met le royaume en premier — dans chaque saison, l'essentiel d'abord, le reste est ajouté.",
        en: "'And all these things will be given to you as well' (Mt 6:33): God provides for the one who puts the Kingdom first — in every season, the essential first, the rest is added.",
      },
    },
    {
      id: "qA3", level: "A",
      prompt: { fr: "Selon Néhémie 2:18, comment le peuple répond-il à l'appel de Néhémie à rebâtir la muraille ?", en: "According to Nehemiah 2:18, how do the people answer Nehemiah's call to rebuild the wall?" },
      choices: [
        { fr: "« Levons-nous, et bâtissons ! »", en: "'Let's rise up and build!'" },
        { fr: "« Attendons des jours meilleurs »", en: "'Let us wait for better days'" },
        { fr: "« Que le roi bâtisse lui-même »", en: "'Let the king build it himself'" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ils dirent : Levons-nous, et bâtissons ! Et ils se fortifièrent dans cette bonne résolution » (Né 2:18) : l'été de la carrière est la saison de bâtir avec courage — c'est pourquoi Néhémie en est le témoin.",
        en: "'They said, Let's rise up and build. So they strengthened their hands for the good work' (Ne 2:18): the summer of career is the season for courageous building — which is why Nehemiah is its witness.",
      },
    },
    {
      id: "qA4", level: "A",
      prompt: { fr: "Selon le Psaume 71:18, que demande le psalmiste arrivé à la vieillesse ?", en: "According to Psalm 71:18, what does the psalmist ask when old age comes?" },
      choices: [
        { fr: "De retrouver les forces de sa jeunesse", en: "To regain the strength of his youth" },
        { fr: "D'annoncer la puissance de Dieu à la génération future", en: "To declare God's power to the generation to come" },
        { fr: "De se reposer loin des plus jeunes", en: "To rest far away from the young" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ne m'abandonne pas… afin que j'annonce ton bras à la génération présente, ta puissance à la génération future ! » (Ps 71:18) : l'hiver de l'aînesse a une mission — transmettre, comme au Banc de la transmission.",
        en: "'Don't forsake me… until I have declared your strength to the next generation, your might to everyone who is to come' (Ps 71:18): the winter of eldership has a mission — passing on, as at the Bench of Passing On.",
      },
    },
    {
      id: "qA5", level: "A",
      prompt: { fr: "Selon Ecclésiaste 3:2, il y a « un temps pour planter, et un temps pour… » ?", en: "According to Ecclesiastes 3:2, there is 'a time to plant, and a time to…'?" },
      choices: [
        { fr: "…récolter sans fin", en: "…harvest without end" },
        { fr: "…replanter ailleurs", en: "…replant elsewhere" },
        { fr: "…arracher ce qui a été planté", en: "…pluck up that which is planted" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Un temps pour planter, et un temps pour arracher ce qui a été planté » (Ec 3:2) : chaque saison a son geste propre ; la sagesse est de discerner le geste de la saison présente.",
        en: "'A time to plant, and a time to pluck up that which is planted' (Ec 3:2): each season has its own action; wisdom is discerning the action of the present season.",
      },
    },
    {
      id: "qE2", level: "E",
      prompt: { fr: "À qui le Psaume 90, qui enseigne à « compter nos jours », est-il attribué ?", en: "To whom is Psalm 90, which teaches us to 'number our days,' attributed?" },
      choices: [
        { fr: "À Moïse, homme de Dieu", en: "To Moses, the man of God" },
        { fr: "À David, le berger devenu roi", en: "To David, the shepherd who became king" },
        { fr: "À Salomon, le roi sage", en: "To Solomon, the wise king" },
      ],
      answerIndex: 0,
      explain: {
        fr: "La suscription du Psaume 90 dit : « Prière de Moïse, homme de Dieu. » Sa vie eut trois saisons de quarante ans — prince en Égypte, berger à Madian, conducteur d'Israël : il savait de quoi il parlait en comptant les jours (Ps 90:12).",
        en: "The heading of Psalm 90 reads: 'A Prayer by Moses, the man of God.' His life had three forty-year seasons — prince in Egypt, shepherd in Midian, leader of Israel: he knew what numbering days meant (Ps 90:12).",
      },
    },
    {
      id: "qE3", level: "E",
      prompt: { fr: "Selon Luc 2:26, quelle promesse le Saint-Esprit avait-il faite à Siméon ?", en: "According to Luke 2:26, what promise had the Holy Spirit made to Simeon?" },
      choices: [
        { fr: "Qu'il deviendrait sacrificateur du temple", en: "That he would become a temple priest" },
        { fr: "Qu'il ne mourrait point avant d'avoir vu le Christ du Seigneur", en: "That he would not die before he had seen the Lord's Christ" },
        { fr: "Que sa descendance serait innombrable", en: "That his descendants would be countless" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Il avait été divinement averti par le Saint-Esprit qu'il ne mourrait point avant d'avoir vu le Christ du Seigneur » (Lc 2:26) : la dernière saison de Siméon était orientée vers une promesse — l'attente confiante, pas le coffre des regrets.",
        en: "'It had been revealed to him by the Holy Spirit that he should not see death before he had seen the Lord's Christ' (Lk 2:26): Simeon's last season was aimed at a promise — confident expectation, not the chest of regrets.",
      },
    },
    {
      id: "qE4", level: "E",
      prompt: { fr: "Quelle exhortation Paul donne-t-il en Éphésiens 5:16 au sujet du temps ?", en: "What exhortation does Paul give in Ephesians 5:16 about time?" },
      choices: [
        { fr: "Compter les années qui nous restent", en: "Count the years we have left" },
        { fr: "Attendre patiemment des temps meilleurs", en: "Wait patiently for better times" },
        { fr: "Racheter le temps, car les jours sont mauvais", en: "Redeem the time, because the days are evil" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Rachetez le temps, car les jours sont mauvais » (Ép 5:16) : le temps est un trésor mesuré à investir dans l'essentiel — même leçon que le cadran solaire et le Psaume 90:12, appliquée par Paul à chaque saison.",
        en: "'Redeeming the time, because the days are evil' (Eph 5:16): time is a measured treasure to invest in the essential — the same lesson as the sundial and Psalm 90:12, applied by Paul to every season.",
      },
    },
    {
      id: "qD6", level: "D",
      prompt: { fr: "Selon Ecclésiaste 3:4, il y a « un temps pour pleurer, et un temps pour… » ?", en: "According to Ecclesiastes 3:4, there is 'a time to weep, and a time to…'?" },
      choices: [
        { fr: "…se taire", en: "…keep silence" },
        { fr: "…travailler", en: "…work" },
        { fr: "…rire", en: "…laugh" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Un temps pour pleurer, et un temps pour rire; un temps pour se lamenter, et un temps pour danser » (Ec 3:4) : les saisons du cœur alternent elles aussi — aucune larme ni aucun rire n'échappe au temps fixé par Dieu.",
        en: "'A time to weep, and a time to laugh; a time to mourn, and a time to dance' (Ec 3:4): the heart's seasons alternate too — no tear and no laughter falls outside God's appointed time.",
      },
    },
    {
      id: "qD7", level: "D",
      prompt: { fr: "Selon Daniel 1:17, que Dieu accorda-t-il à Daniel et à ses trois compagnons ?", en: "According to Daniel 1:17, what did God grant Daniel and his three companions?" },
      choices: [
        { fr: "De la science, de l'intelligence et de la sagesse", en: "Knowledge, understanding and wisdom" },
        { fr: "Des richesses et de longs jours", en: "Riches and long life" },
        { fr: "Le commandement des armées du roi", en: "Command of the king's armies" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Dieu accorda à ces quatre jeunes gens de la science, de l'intelligence dans toutes les lettres, et de la sagesse » (Dn 1:17) : au printemps des études, la fidélité de Daniel et de ses trois amis fut couronnée par un don de Dieu — pas seulement par l'effort.",
        en: "'God gave these four young men knowledge and skill in all learning and wisdom' (Dn 1:17): in the springtime of studies, the faithfulness of Daniel and his three friends was crowned by God's gift — not by effort alone.",
      },
    },
    {
      id: "qD8", level: "D",
      prompt: { fr: "Selon Josué 24:15, que déclare Josué au sujet de sa maison ?", en: "According to Joshua 24:15, what does Joshua declare about his house?" },
      choices: [
        { fr: "« Ma maison bâtira le temple de l'Éternel »", en: "'My house will build Yahweh's temple'" },
        { fr: "« Moi et ma maison, nous servirons l'Éternel »", en: "'As for me and my house, we will serve Yahweh'" },
        { fr: "« Ma maison jugera les tribus d'Israël »", en: "'My house will judge the tribes of Israel'" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Choisissez aujourd'hui qui vous voulez servir… Moi et ma maison, nous servirons l'Éternel » (Jos 24:15) : le choix d'essentiel de l'automne — la présence aux siens — commence par cette décision posée pour tout le foyer.",
        en: "'Choose today whom you will serve… but as for me and my house, we will serve Yahweh' (Jos 24:15): autumn's essential choice — presence with your own — begins with this decision made for the whole household.",
      },
    },
    {
      id: "qD9", level: "D",
      prompt: { fr: "Selon Luc 2:28, que fait Siméon lorsqu'il voit enfin l'enfant Jésus au temple ?", en: "According to Luke 2:28, what does Simeon do when he finally sees the child Jesus in the temple?" },
      choices: [
        { fr: "Il court avertir les sacrificateurs", en: "He runs to alert the priests" },
        { fr: "Il tombe le visage contre terre", en: "He falls face down to the ground" },
        { fr: "Il le reçoit dans ses bras et bénit Dieu", en: "He receives him in his arms and blesses God" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Il le reçut dans ses bras, bénit Dieu, et dit : Maintenant, Seigneur, tu laisses ton serviteur s'en aller en paix » (Lc 2:28-29) : l'hiver de Siméon s'achève non par des regrets mais par une louange comblée — la promesse tenue dans ses bras.",
        en: "'He received him into his arms, and blessed God, and said, Now you are releasing your servant, Master, in peace' (Lk 2:28-29): Simeon's winter ends not in regrets but in fulfilled praise — the promise held in his own arms.",
      },
    },
    {
      id: "qA6", level: "A",
      prompt: { fr: "Selon Luc 2:36-37, comment Anne la prophétesse, âgée de quatre-vingt-quatre ans, servait-elle Dieu ?", en: "According to Luke 2:36-37, how did Anna the prophetess, eighty-four years old, serve God?" },
      choices: [
        { fr: "Nuit et jour, dans le jeûne et la prière, sans quitter le temple", en: "Night and day, with fastings and prayers, never leaving the temple" },
        { fr: "En enseignant la Loi aux enfants de Jérusalem", en: "By teaching the Law to Jerusalem's children" },
        { fr: "En cousant des vêtements pour les veuves", en: "By sewing garments for the widows" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Elle ne quittait pas le temple, et elle servait Dieu nuit et jour dans le jeûne et dans la prière » (Lc 2:37) : aux côtés de Siméon, Anne la prophétesse montre que l'hiver de la vie peut être la saison la plus consacrée — la prière fidèle ne prend pas sa retraite.",
        en: "'She didn't depart from the temple, worshipping with fastings and petitions night and day' (Lk 2:37): alongside Simeon, Anna the prophetess shows that life's winter can be the most consecrated season — faithful prayer never retires.",
      },
    },
    {
      id: "qA7", level: "A",
      prompt: { fr: "Selon Néhémie 2:4, que fait Néhémie avant de répondre à la question du roi Artaxerxès ?", en: "According to Nehemiah 2:4, what does Nehemiah do before answering King Artaxerxes' question?" },
      choices: [
        { fr: "Il consulte les chefs de Juda", en: "He consults the leaders of Judah" },
        { fr: "Il demande un délai de trois jours", en: "He asks for three days' delay" },
        { fr: "Il prie le Dieu des cieux", en: "He prays to the God of heaven" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Le roi me dit : Que demandes-tu ? Je priai le Dieu des cieux, et je répondis au roi » (Né 2:4-5) : entre la question du roi et sa réponse, Néhémie glisse une prière éclair — dans l'été de la carrière, l'essentiel se joue avant de parler.",
        en: "'Then the king said to me, What is your request? So I prayed to the God of heaven, and I said to the king' (Ne 2:4-5): between the king's question and his answer, Nehemiah slips in a flash prayer — in career's summer, the essential happens before speaking.",
      },
    },
    {
      id: "qA8", level: "A",
      prompt: { fr: "Selon Ecclésiaste 3:11, qu'est-ce que Dieu a mis dans le cœur de l'homme ?", en: "According to Ecclesiastes 3:11, what has God set in the human heart?" },
      choices: [
        { fr: "L'oubli des saisons passées", en: "Forgetfulness of past seasons" },
        { fr: "La pensée de l'éternité", en: "Eternity" },
        { fr: "La crainte du lendemain", en: "The fear of tomorrow" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Il fait toute chose bonne en son temps; même il a mis dans leur cœur la pensée de l'éternité » (Ec 3:11) : c'est pourquoi aucune saison terrestre ne comble à elle seule — le cœur est fait pour plus grand que le temps.",
        en: "'He has made everything beautiful in its time. He has also set eternity in their hearts' (Ec 3:11): that is why no earthly season fully satisfies on its own — the heart is made for something greater than time.",
      },
    },
    {
      id: "qE5", level: "E",
      prompt: { fr: "Selon Néhémie 6:15, en combien de jours la muraille de Jérusalem fut-elle achevée ?", en: "According to Nehemiah 6:15, in how many days was the wall of Jerusalem finished?" },
      choices: [
        { fr: "En quarante jours", en: "In forty days" },
        { fr: "En cinquante-deux jours", en: "In fifty-two days" },
        { fr: "En soixante-dix jours", en: "In seventy days" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« La muraille fut achevée le vingt-cinquième jour du mois d'Elul, en cinquante-deux jours » (Né 6:15) : une saison de bâtisseur courte mais entièrement investie — l'été de la carrière ne se mesure pas à sa durée, mais à sa fidélité.",
        en: "'So the wall was finished in the twenty-fifth day of the month Elul, in fifty-two days' (Ne 6:15): a builder's season short yet fully invested — career's summer is measured not by its length but by its faithfulness.",
      },
    },
    {
      id: "qE6", level: "E",
      prompt: { fr: "Selon Daniel 6:10, combien de fois par jour Daniel se mettait-il à genoux pour prier, malgré le décret du roi ?", en: "According to Daniel 6:10, how many times a day did Daniel kneel to pray, despite the king's decree?" },
      choices: [
        { fr: "Une fois, au lever du soleil", en: "Once, at sunrise" },
        { fr: "Sept fois, comme le psalmiste", en: "Seven times, like the psalmist" },
        { fr: "Trois fois le jour", en: "Three times a day" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Trois fois le jour il se mettait à genoux, il priait, et il louait son Dieu, comme il le faisait auparavant » (Dn 6:10) : la résolution du jeune étudiant (Dn 1:8) est devenue l'habitude de toute une vie — l'essentiel choisi au printemps tient jusqu'à l'hiver.",
        en: "'He kneeled on his knees three times a day, and prayed, and gave thanks before his God, as he did before' (Dn 6:10): the young student's resolve (Dn 1:8) became a lifelong habit — the essential chosen in spring holds all the way to winter.",
      },
    },
    {
      id: "qE7", level: "E",
      prompt: { fr: "Selon le Psaume 92:13-15, que font encore, dans la vieillesse, les justes plantés dans la maison de l'Éternel ?", en: "According to Psalm 92:12-14, what do the righteous planted in Yahweh's house still do in old age?" },
      choices: [
        { fr: "Ils portent des fruits, pleins de sève et verdoyants", en: "They still produce fruit, full of sap and green" },
        { fr: "Ils se reposent à l'ombre du palmier", en: "They rest in the palm tree's shade" },
        { fr: "Ils racontent leurs exploits passés", en: "They recount their past exploits" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Plantés dans la maison de l'Éternel… ils portent encore des fruits dans la vieillesse, ils sont pleins de sève et verdoyants » (Ps 92:14-15) : l'hiver de l'aînesse n'est pas une saison stérile — enraciné en Dieu, on fructifie jusqu'au bout.",
        en: "'They are planted in Yahweh's house… They will still produce fruit in old age. They will be full of sap and green' (Ps 92:13-14): the winter of eldership is no barren season — rooted in God, one bears fruit to the very end.",
      },
    },
  ],
  tips: [
    { fr: "Quatre saisons, quatre autels : dépose chaque sphère d'or sur l'autel de sa saison — jamais sur le divan, l'idole, le trône ou le coffre.", en: "Four seasons, four altars: place each gold sphere on its season's altar — never on the couch, the idol, the throne or the chest." },
    { fr: "Les 4 fruits de saison et les 4 sabliers rappellent que chaque âge a son trésor propre (Ec 3:1).", en: "The 4 season fruits and the 4 hourglasses remind you that every age has its own treasure (Ec 3:1)." },
    { fr: "Au centre, actionne la fontaine, le cadran solaire et la cloche du soir : compte tes jours et cherche premièrement le royaume (Ps 90:12 ; Mt 6:33).", en: "At the center, work the fountain, the sundial and the evening bell: number your days and seek first the Kingdom (Ps 90:12; Mt 6:33)." },
  ],
  lesson: {
    fr: "Cherchez premièrement le royaume et la justice de Dieu; et toutes ces choses vous seront données par-dessus. (Matthieu 6:33)",
    en: "But seek first God's Kingdom, and his righteousness; and all these things will be given to you as well. (Matthew 6:33)",
  },
  audio: { ambience: "garden" },
  multiplayer: { mode: "coop", maxPlayers: 8 },
};

export default vieDimpactScene3;
